import React, { useState, useRef, useEffect } from 'react';
import { FaCheck } from 'react-icons/fa';

const EnvironmentCarousel = ({ environments, selectedEnv, setSelectedEnv }) => {
  const [visibleItems, setVisibleItems] = useState(3); // Number of visible items based on container width

  // Determine number of visible items based on container width
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 400) {
        setVisibleItems(1);
      } else if (width < 600) {
        setVisibleItems(2);
      } else {
        setVisibleItems(3);
      }
    };

    handleResize(); // Initial call
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePrev = () => {
    const currentIndex = environments.findIndex(env => env.name === selectedEnv.name);
    const prevIndex = (currentIndex - 1 + environments.length) % environments.length;
    const prevEnv = environments[prevIndex];
    setSelectedEnv({
      name: prevEnv.name,
      url: prevEnv.fullImage,
      color: prevEnv.color
    });
  };

  const handleNext = () => {
    const currentIndex = environments.findIndex(env => env.name === selectedEnv.name);
    const nextIndex = (currentIndex + 1) % environments.length;
    const nextEnv = environments[nextIndex];
    setSelectedEnv({
      name: nextEnv.name,
      url: nextEnv.fullImage,
      color: nextEnv.color
    });
  };

  return (
    <div style={{
      width: "100%",
      boxSizing: "border-box",
      overflow: "hidden" // Ensure no overflow at the root level
    }}>
      <div style={{
        display: "flex",
        marginBottom: 15,
        paddingBottom: 10,
        width: "100%",
        paddingTop: 15,
        alignItems: "center",
        boxSizing: "border-box",
        justifyContent: "space-between",
        position: "relative"
      }}>
        {/* Left Arrow */}
        <div 
          style={{
            width: 30,
            height: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: "50%",
            background: "rgba(0, 0, 0, 0.3)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            zIndex: 2,
            flexShrink: 0
          }}
          onClick={handlePrev}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
          </svg>
        </div>

        {/* Carousel Container */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "calc(100% - 70px)",
          overflow: "hidden",
          position: "relative"
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 0.3s ease",
            width: "100%",
            overflowX: "visible"
          }}>
            {environments.length && selectedEnv && environments.map((env, index) => {
              const isSelected = env.name === selectedEnv.name;
              const currentIndex = environments.findIndex(e => e.name === selectedEnv.name);
              
              // Calculate if this item should be visible
              let distance = Math.abs(index - currentIndex);
              if (distance > Math.floor(visibleItems / 2)) {
                // Check if we can wrap around (for circular navigation)
                const wrapDistance = Math.abs(environments.length - Math.abs(index - currentIndex));
                if (wrapDistance < distance) {
                  distance = wrapDistance;
                }
              }
              
              // Only render items that should be visible
              const shouldBeVisible = distance <= Math.floor(visibleItems / 2);
              
              if (!shouldBeVisible) return null;
              
              return (
              <div 
                key={env.name}
                style={{
                  display: "flex",
                  minHeight: 0,
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  borderRadius: 8,
                  position: "relative",
                  cursor: "pointer",
                  border: `2px solid ${isSelected ? "#3b725f" : 'rgba(255, 255, 255, 0.3)'}`,
                  padding: 5,
                  paddingBottom: 8,
                  boxSizing: "border-box",
                  marginRight: 8,
                  marginLeft: 8,
                  transform: isSelected ? "scale(1.05)" : "scale(1)",
                  transition: "all 0.2s ease",
                  opacity: isSelected ? 1 : 0.7,
                  width: 95,
                  flexShrink: 0,
                  overflow: "visible"
                }} 
                onClick={() => {
                  setSelectedEnv({
                    name: env.name,
                    url: env.fullImage,
                    color: env.color
                  });
                }}
              >
                <div style={{
                  width: 85,
                  height: 85,
                  borderRadius: 5,
                  backgroundImage: env.name === "Cabinet" ? `url(default-env-cabinet.jpg)` : (env.color ? 'none' : `url(${env.thumbnail})`),
                  backgroundColor: env.color || '',
                  backgroundSize: "cover",
                  border: env.color ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
                }} />
                <div style={{ width: "100%", height: 2, marginTop: 3, borderBottom: '1px dotted rgba(255, 255, 255, 0.5)'}} />
                {isSelected ? <div style={{
                  position: "absolute",
                  bottom: 5,
                  right: 5,
                  borderRadius: "50%",
                  width: 20,
                  height: 20,
                  background: "#3b725f",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(59, 114, 95, 0.5)",
                  border: "2px solid white"
                }}><FaCheck style={{
                  fontSize: 10,
                  color: "white"
                }} /></div> : null}
                <div style={{
                  fontSize: 15,
                  marginTop: 4,
                  maxWidth: 85,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }} title={env.name}>
                  {env.name}
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Right Arrow */}
        <div 
          style={{
            width: 30,
            height: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: "50%",
            background: "rgba(0, 0, 0, 0.3)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            zIndex: 2,
            flexShrink: 0
          }}
          onClick={handleNext}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
          </svg>
        </div>
      </div>
      
    </div>
  );
};

export default EnvironmentCarousel;